import { LayerOption } from 'components';
import * as Styled from './styles';
import { useEffect, useState } from 'react';
import { FaFilter } from "react-icons/fa";
import { FiltroAvancado } from './FiltroAvancado';
import { FiltroSimples } from './FiltroSimples';
import { getSimpleFilterValues, IFilter } from 'services/filterRequests';
import { IRows } from 'contexts/@types/TabelaAtributos';
import { ILayers } from 'contexts/@types/Layers';
import { useTabelaAtributos } from 'hooks/useTabelaAtributos';
import { useZIndex } from 'hooks/useZIndex';
import { captureMonitoringEvent } from 'services/monitoring';

interface IModalFilter {
  rows: IRows;
  onClose: () => void;
  onSelect: () => void;
  onFilter: (query: string, layerUuid?: string) => void;
  appliedFilters: Record<string, string | null>;
}

export function ModalFilters({ rows, onClose, onSelect, onFilter, appliedFilters }: IModalFilter) {

  const modalFilterPosition = { posX: 400, posY: 200 };
  const [advancedFilter, setAdvancedFilter] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedLayerUuid, setSelectedLayerUuid] = useState<string>('');

  const [filters, setFilters] = useState<IFilter[]>([]);

  const { tabs, currentTab } = useTabelaAtributos();
  const { bringToFront, getZIndex } = useZIndex();

  function getLayerUuid(layer: ILayers) {
    return `${layer.layertitle}${layer.layer_filter_id || ''}`;
  }

  useEffect(() => {
    bringToFront('map-filters-modal');
  }, [bringToFront])

  function getPosX(): number {
    const pos = modalFilterPosition.posX - (window.innerWidth >= 1280 ? 300 : 100);
    return Math.min(pos + 25, window.innerWidth - 280);
  }

  function getPosY(): number {
    const pos = modalFilterPosition.posY + (window.innerWidth >= 1280 ? 0 : 50);
    return Math.min(pos - 150, window.innerHeight - 160);
  }

  useEffect(() => {
    setLoading(true);
    const filterPromises = tabs.map(tab => {
      if (!tab?.layer?.is_vector && !tab?.layer?.vector_source) {
        return getSimpleFilterValues(tab.layer.id, tab.layer_query_filter)
          .then(response => response.map(filter => ({
            ...filter,
            layerUuid: getLayerUuid(tab.layer),
            layerId: tab.layer.id,
            layerTitle: tab.layerTitle,
          })));
      }
      return Promise.resolve([]);
    });

    Promise.all(filterPromises)
      .then(responses => {
        setFilters(responses.flat()); 
      })
      .catch(error => {
        console.error("Error fetching filters:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [tabs, ])

  useEffect(() => {
    if (selectedLayerUuid && tabs.findIndex(tabItem => getLayerUuid(tabItem.layer) === selectedLayerUuid) < 0) {
      setSelectedLayerUuid('');
    }
  }, [tabs, selectedLayerUuid])

  const appliedFilter = selectedLayerUuid ? (appliedFilters[selectedLayerUuid] ?? null) : null;

  function handleFilter(query: string, layerUuid?: string, registrar = false) {
    const targetUuid = layerUuid || selectedLayerUuid || undefined;
    onFilter(query, targetUuid);

    if (registrar && query.trim() && targetUuid) {
      const tab = tabs.find(tabItem => getLayerUuid(tabItem.layer) === targetUuid);
      if (tab?.layer) {
        void captureMonitoringEvent({
          tipo_evento: 'filtro_aplicado',
          identificador_camada: tab.layer.layername || String(tab.layer.id),
          tipo_filtro: advancedFilter ? 'avancado' : 'simples',
        });
      }
    }
  }

  function handleModeChange(nextAdvanced: boolean) {
    if (nextAdvanced === advancedFilter) return;
    setAdvancedFilter(nextAdvanced);
    onFilter('', undefined);
  }

  return (
    <>
      <Styled.Wrapper
        zIndex={getZIndex('map-filters-modal')}
        onMouseDown={() => bringToFront('map-filters-modal')}
        onTouchStart={() => bringToFront('map-filters-modal')}
      >
        <Styled.Features style={{ pointerEvents: 'none' }}>
          <Styled.Modal>
            <LayerOption
              defaultPos={{x: getPosX(), y: getPosY()}}
              title={
                <>
                  <div id="btn-subtitle">
                    <FaFilter size={22}/>
                  </div>
                  <p id="title-dragglable">Filtros</p>
                </>
              }
              index={0}
              pages={0}
              defaultSize={{width: advancedFilter? 700 : 500, height: 660}}
              zoom={false}
              resizable={false}
              onClose={onClose}
              content={(
                <div>
                  <Styled.Header>
                    <Styled.Tab active={!advancedFilter} onClick={() => handleModeChange(false)}>Filtro Simples</Styled.Tab>
                    <Styled.Tab style={{borderLeft: 0}} active={advancedFilter} onClick={() => handleModeChange(true)}>Filtro Avancado</Styled.Tab>
                  </Styled.Header>
                  { advancedFilter? 
                    <FiltroAvancado
                      rows={rows}
                      onFilter={handleFilter}
                      appliedFilter={appliedFilter}
                      selectedLayerUuid={selectedLayerUuid}
                      onSelectLayer={setSelectedLayerUuid}
                    /> :
                    <FiltroSimples
                      currentTab={currentTab}
                      currentLayerUuid={tabs[currentTab]?.layer ? getLayerUuid(tabs[currentTab].layer) : ''}
                      loading={loading}
                      onSelect={onSelect}
                      onFilter={handleFilter}
                      filters={filters}
                      appliedFilters={appliedFilters}
                    />
                  }
                </div>
              )}
            />
          </Styled.Modal>
        </Styled.Features>
      </Styled.Wrapper>
    </>
  );
}
